/**
 * PicksView - AI-Powered Parlay Recommendations
 * Main view for generating and viewing AI picks
 */

import { BettingTipBanner } from '@components/ui/BettingTipBanner';
import ErrorModal from '@components/ui/ErrorModal';
import { authAwareFetch } from '@contexts/authHttpUtils';
import { BETTING_TIPS } from '@parlay-picker/shared/bettingTips';
import type { ListedGame } from '@parlay-picker/shared/hooks';
import {
  filterGamesByLeague,
  gamesWithKnownLeague,
  type League,
  leagueKeyToLeague,
  leagueToLeagueKey,
  parseLeagueKey,
} from '@parlay-picker/shared/league';
import type { LlmProvider } from '@parlay-picker/shared/llmProviders';
import { getBookDisplayName, sportsbookForLeague } from '@parlay-picker/shared/sportsbook';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type ProviderSelection = LlmProvider | 'auto';

import { useAuth } from '@contexts/AuthContext';
import { useQuotaCoin } from '@contexts/QuotaCoinContext';
import { useCurrentLeagueKey } from '@hooks/useCurrentLeagueKey';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { reportUnknownLeagueGame } from '@hooks/useGamesAndProps';
import { useOddsComparison } from '@hooks/useOddsComparison';
import { usePicksStream } from '@hooks/usePicksStream';
import { useStrategyStats } from '@hooks/useStrategyStats';
import type { AnalysisMode } from '@parlay-picker/shared/analysisMode';
import { DEFAULT_PRESET_ID } from '@parlay-picker/shared/configPreset';
import { getAvailableMarketsForLeague } from '@parlay-picker/shared/marketKey';
import { getQuotaForMode } from '@parlay-picker/shared/subscriptionTier';
import { useAnalysisConfigStore } from '@stores/analysisConfigStore';
import { useGenerationStore, useQuotaUsage, useStreamingParlays } from '@stores/generationStore';
import { useGlobalShortcutsStore } from '@stores/globalShortcutsStore';
import { useParlayBuilderStore } from '@stores/parlayBuilderStore';
import { usePresetsStore } from '@stores/presetsStore';
import { getUserFriendlyError, safeParseJson } from '@utils/api';
import { API_ENDPOINTS, apiUrl } from '../../config/api';
import { BankrollSimPanel } from './components/BankrollSimPanel';
import { ClvTrendsPanel } from './components/ClvTrendsPanel';
import { MarketIntelligence } from './components/MarketIntelligence';
import { PicksConfigSection } from './components/PicksConfigSection';
import { PicksDashboardHero } from './components/PicksDashboardHero';
import { PicksModals } from './components/PicksModals';
import { PicksResultsSection } from './components/PicksResultsSection';
import { PicksViewToggle } from './components/PicksViewToggle';
import { StreamingPicksPreview } from './components/StreamingPicksPreview';
import { TrackedParlaysSummaryWidget } from './components/TrackedParlaysSummaryWidget';
import {
  AVAILABLE_MARKETS,
  DEFAULT_ANALYSIS_CONFIG,
  DEFAULT_FILTERS,
  type MarketOption,
  RISK_TIERS,
} from './constants';
import { generatePicksFn, refreshScoresOnlyFn } from './generatePicksFn';
import { useBannedPlayers } from './hooks/useBannedPlayers';
import { useColdStartRecovery } from './hooks/useColdStartRecovery';
import { useNetworkRecovery } from './hooks/useNetworkRecovery';
import { useParlayTracking } from './hooks/useParlayTracking';
import { usePicksHealthCheck } from './hooks/usePicksHealthCheck';
import {
  filterParlays,
  type Game,
  groupByRiskTier,
  groupGamesByDay,
  type RiskTierGroup,
} from './utils';

// — Types —

import type { TrackedLeg } from '@hooks/useLegs';
import type {
  NewParlay,
  ParlayStats,
  TrackedParlay,
  TrackParlayResult,
} from '@hooks/useTrackedParlays';

// Import chart click data type from ParlayCard
import type { ChartClickData } from './components/ParlayCard';

// Use same type for modal data
type ChartModalData = ChartClickData;

interface ErrorData {
  message: string;
  type: string;
  retryAfter?: number;
}

interface CalibrationThresholds {
  betThreshold: number;
  skipThreshold: number;
  confidenceLevel: 'high' | 'medium' | 'low';
  sampleCount: number;
}

interface PicksViewProps {
  legs?: TrackedLeg[];
  trackedParlays?: TrackedParlay[];
  trackedParlayStats?: ParlayStats;
  trackParlay?: (data: NewParlay) => Promise<TrackParlayResult>;
  onNavigateToParlays?: () => void;
}

// — Component —

const DEFAULT_STATS: ParlayStats = {
  total: 0,
  won: 0,
  lost: 0,
  pending: 0,
  voided: 0,
  aiCount: 0,
  manualCount: 0,
  betCount: 0,
};

export function PicksView({
  legs = [],
  trackedParlays = [],
  trackedParlayStats = DEFAULT_STATS,
  trackParlay: trackParlayProp,
  onNavigateToParlays,
}: PicksViewProps): React.ReactElement {
  // Auth context for database persistence
  const { user, authFetch, updatePreferences } = useAuth();

  const { showCoin } = useQuotaCoin();

  // Sync analysisConfig to builder store (for tracking + analysis enrichment)
  const { setAnalysisConfig: syncBuilderConfig } = useParlayBuilderStore();

  // Feature access — hide line shopping for free users or when user disabled it
  const { isLocked } = useFeatureAccess();
  const oddsLocked =
    isLocked('odds_comparison') || user?.preferences?.enableLineComparison === false;

  // Best Line Finder — multi-book odds comparison
  const { comparisons: oddsComparisons, fetchComparison } = useOddsComparison();

  // Dynamic strategy stats from database
  // Admin-only: the underlying lab endpoints 403 for non-admins, so only fetch for admins.
  const { stats: strategyStats, predictionStats } = useStrategyStats({
    enabled: user?.role === 'admin',
  });

  // Streaming pick generation (SSE)
  const { startStream: _startStream, onQuotaRef } = usePicksStream();

  // Wire SSE stream quota callback to coin notification
  onQuotaRef.current = (quota) => {
    if (quota) showCoin(quota);
  };

  // Extracted hooks
  const { apiHealth, setApiHealth, startHealthPolling, checkApiHealth } = usePicksHealthCheck();
  const currentLeagueKey = useCurrentLeagueKey();
  const [error, setError] = useState<ErrorData | null>(null);
  const {
    bannedPlayers,
    availablePlayers,
    loadingPlayers,
    addToBanList,
    addPlayersToBanList,
    removeFromBanList,
    removePlayersFromBanList,
  } = useBannedPlayers(currentLeagueKey, setError);

  // Generation and picks state from Zustand store
  const {
    isGenerating,
    isDismissed: isGenerationDismissed,
    activePicks,
    pendingPicks,
    startGeneration,
    finishGeneration,
    cancelGeneration,
    dismissModal: dismissGenerationModal,
    setActivePicks,
    setPendingPicks,
    applyPendingPicks,
    hasActivePicks,
    getAutoApplyPicks,
    setAutoApplyPicks,
    setAutoGroupByTier,
    collapseState,
    toggleTierCollapse,
    toggleParlayCollapse,
    saveToHistory,
    loadHistoryFromServer,
  } = useGenerationStore();

  // Streaming parlays for tab toggle count
  const streamingParlays = useStreamingParlays();

  // Quota usage
  const quotaUsage = useQuotaUsage();
  const { fetchQuotaUsage, decrementQuotaLocally } = useGenerationStore();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Load generation history from server when user is available (cross-device sync)
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run only when user identity changes
  useEffect(() => {
    if (user) {
      loadHistoryFromServer();
    }
  }, [user?.id]);

  // Network drop recovery - auto-recover picks when network resumes
  useNetworkRecovery({
    isGenerating,
    onPicksRecovered: (picks, generationId) => {
      setActivePicks({
        picks: picks as never,
        scores: [],
        generationId,
        cached: true,
        statsWarning: null,
        lineMismatchWarning: null,
        leagueKey: leagueToLeagueKey(selectedLeague),
      });
    },
  });

  // Cold-start recovery - detect in-progress generation on page load (e.g. tab closed during generation)
  useColdStartRecovery({
    onPicksRecovered: useCallback(
      (picks: unknown, recoveredGenerationId: string) => {
        setActivePicks({
          picks: picks as never,
          scores: [],
          generationId: recoveredGenerationId,
          cached: true,
          statsWarning: null,
          lineMismatchWarning: null,
          leagueKey: useGenerationStore.getState().generationLeagueKey,
        });
      },
      [setActivePicks],
    ),
    onGenerationResumed: useCallback((startedAt: number | null) => {
      useGenerationStore.setState({
        isGenerating: true,
        currentPhase: 'generating',
        phaseMessage: 'Resuming generation...',
        generationStartTime: startedAt,
      });
    }, []),
    onGenerationEnded: useCallback(() => {
      useGenerationStore.setState({
        isGenerating: false,
        phaseMessage: null,
      });
    }, []),
  });

  // Derived values from store — memoize to prevent useEffect re-triggers on unrelated re-renders
  const recs = useMemo(() => activePicks?.picks ?? [], [activePicks?.picks]);
  const inlineScores = useMemo(() => activePicks?.scores ?? [], [activePicks?.scores]);
  const generationId = activePicks?.generationId ?? null;
  const wasCached = activePicks?.cached ?? false;
  const statsWarning = activePicks?.statsWarning ?? null;

  // Refs
  const parlaysRef = useRef<HTMLDivElement>(null);

  // Core state
  const [selectedLeague, setSelectedLeague] = useState<League>(() =>
    leagueKeyToLeague(currentLeagueKey),
  );

  // The user's book for the selected league, for odds comparison, tracking and display
  const selectedSportsbook = sportsbookForLeague(
    user?.preferences,
    leagueToLeagueKey(selectedLeague),
  );
  const sportsbookName = getBookDisplayName(selectedSportsbook);

  // Sync league from auth context (e.g., when changed via config panel)
  const preferredLeagueKey = parseLeagueKey(user?.preferences?.preferredLeague);
  useEffect(() => {
    if (!preferredLeagueKey) return;
    const league = leagueKeyToLeague(preferredLeagueKey);
    setSelectedLeague((prev) => {
      if (prev === league) return prev;
      setSelectedGames([]);
      // Auto-switch preset if current one doesn't match the new league
      const { presets, activePresetId, switchPreset: switchToPreset } = usePresetsStore.getState();
      const activePreset = presets.find((p) => p.id === activePresetId);
      if (activePreset?.league && activePreset.league !== preferredLeagueKey) {
        switchToPreset(DEFAULT_PRESET_ID);
      }
      return league;
    });
  }, [preferredLeagueKey]);

  const [loadingGames, setLoadingGames] = useState<boolean>(true);
  const [refreshingGames, setRefreshingGames] = useState<boolean>(false);
  const [games, setGames] = useState<Game[]>([]);
  const [selectedGames, setSelectedGames] = useState<string[]>([]);
  const [lastFreshFetch, setLastFreshFetch] = useState<Date | null>(null);

  // Chart modal state
  const [chartModal, setChartModal] = useState<ChartModalData | null>(null);

  // Config panel collapses once results are available
  const [isConfigCollapsed, setIsConfigCollapsed] = useState(false);

  // View toggle: switch between current picks and streaming preview
  const [viewMode, setViewMode] = useState<'current' | 'streaming'>('current');

  // Auto-switch to current when generation completes
  useEffect(() => {
    if (!isGenerating && viewMode === 'streaming') {
      setViewMode('current');
    }
  }, [isGenerating, viewMode]);

  // Lock body scroll when config panel is expanded fullscreen
  const isConfigFullscreen = recs.length > 0 && !isConfigCollapsed;
  const setPicksConfigFullscreen = useGlobalShortcutsStore((s) => s.setPicksConfigFullscreen);
  const closeConfigPanel = useGlobalShortcutsStore((s) => s.closeConfigPanel);
  useEffect(() => {
    setPicksConfigFullscreen(isConfigFullscreen);
    if (isConfigFullscreen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
        setPicksConfigFullscreen(false);
      };
    }
  }, [isConfigFullscreen, setPicksConfigFullscreen]);

  // Filter state
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  // UI toggle for detailed analysis
  const [showDetailedAnalysis, setShowDetailedAnalysis] = useState<boolean>(false);

  // Admin-only: LLM provider selection
  const [selectedProvider, setSelectedProvider] = useState<ProviderSelection>('auto');

  // Analysis config state - from global store
  const analysisConfig = useAnalysisConfigStore((s) => s.config);
  const {
    setConfig: setAnalysisConfig,
    loadFromUser: loadConfigFromUser,
    setSettledLegCount,
  } = useAnalysisConfigStore();

  // Filter available markets by selected league (pass LeagueKey)
  const leagueMarkets = getAvailableMarketsForLeague(leagueToLeagueKey(selectedLeague));

  // Derive selectedMarkets from analysisConfig.propTypes (scoped to current league)
  const selectedMarkets: string[] = leagueMarkets
    .filter(
      (m: MarketOption) =>
        analysisConfig.propTypes?.[m.key] ?? DEFAULT_ANALYSIS_CONFIG.propTypes?.[m.key] ?? false,
    )
    .map((m: MarketOption) => m.key);

  const setSelectedMarkets = (updater: string[] | ((prev: string[]) => string[])): void => {
    setAnalysisConfig((prev) => {
      const newMarkets =
        typeof updater === 'function'
          ? updater(AVAILABLE_MARKETS.filter((m) => prev.propTypes?.[m.key]).map((m) => m.key))
          : updater;
      const newPropTypes: Record<string, boolean> = {};
      for (const market of AVAILABLE_MARKETS) {
        newPropTypes[market.key] = newMarkets.includes(market.key);
      }
      return { ...prev, propTypes: newPropTypes };
    });
  };

  // Sync analysisConfig to builder context
  useEffect(() => {
    syncBuilderConfig(analysisConfig as Record<string, unknown>);
  }, [analysisConfig, syncBuilderConfig]);

  // Update settled leg count in store
  useEffect(() => {
    const settledCount = legs.filter((leg) => leg.hit !== null && leg.hit !== undefined).length;
    setSettledLegCount(settledCount);
  }, [legs, setSettledLegCount]);

  // Calibration state (for score display)
  const [calibration, setCalibration] = useState<{
    strategy: CalibrationThresholds | null;
    prediction: CalibrationThresholds | null;
  } | null>(null);

  useEffect(() => {
    if (!user) return;
    authFetch(apiUrl(API_ENDPOINTS.SCORING_CALIBRATION), {
      headers: { 'Content-Type': 'application/json' },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) setCalibration(data);
      })
      .catch(() => {});
  }, [user, authFetch]);

  // Sync from the user's stored config when they log in (the server has cleaned it to their plan)
  const userId = user?.id;
  useEffect(() => {
    if (!userId) return;
    loadConfigFromUser(user?.analysisConfig ?? null);
  }, [userId, user?.analysisConfig, loadConfigFromUser]);

  // Load config presets from user's DB record
  const loadPresetsFromUser = usePresetsStore((s) => s.loadFromUser);
  useEffect(() => {
    if (user?.configPresets) {
      loadPresetsFromUser(user.configPresets);
    }
  }, [user?.configPresets, loadPresetsFromUser]);

  const fetchGames = useCallback(async (): Promise<void> => {
    setLoadingGames(true);
    try {
      const res = await authFetch(apiUrl(API_ENDPOINTS.GAMES));
      const data = (await safeParseJson(res)) as {
        error?: string;
        games?: ListedGame[];
      };
      if (data.error) throw new Error(data.error);
      setGames(gamesWithKnownLeague(data.games || [], reportUnknownLeagueGame));
    } catch (err) {
      setError({ message: getUserFriendlyError(err as Error), type: 'generic' });
    } finally {
      setLoadingGames(false);
    }
  }, [authFetch]);

  const handleRefreshGames = useCallback(async (): Promise<void> => {
    setRefreshingGames(true);
    try {
      await fetchGames();
    } finally {
      setRefreshingGames(false);
    }
  }, [fetchGames]);

  // Fetch games and quota on mount
  useEffect(() => {
    fetchGames();
    fetchQuotaUsage(authFetch);
  }, [authFetch, fetchGames, fetchQuotaUsage]);

  // Prune stale selectedGames IDs when games list changes
  useEffect(() => {
    if (games.length === 0) return;
    const validIds = new Set(games.map((g) => g.id || ''));
    setSelectedGames((prev) => {
      const pruned = prev.filter((id) => validIds.has(id));
      return pruned.length === prev.length ? prev : pruned;
    });
  }, [games]);

  // Apply pending picks when the user clicks the "Apply" banner
  const handleApplyPending = useCallback(() => {
    applyPendingPicks();
    setTimeout(
      () => parlaysRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      100,
    );
  }, [applyPendingPicks]);

  // Sync autoApplyPicks store from user preferences on initial mount
  const hasInitializedAutoApplyRef = useRef(false);
  useEffect(() => {
    if (!hasInitializedAutoApplyRef.current && user) {
      hasInitializedAutoApplyRef.current = true;
      const userPref = user.preferences?.autoApplyPicks ?? false;
      setAutoApplyPicks(userPref);
    }
  }, [user, setAutoApplyPicks]);

  // Sync animation preference to store (respects prefers-reduced-motion)
  useEffect(() => {
    if (!user) return;
    const userPref = user.preferences?.enablePremiumAnimations ?? true;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    setAutoGroupByTier(userPref && !prefersReducedMotion);
  }, [user, setAutoGroupByTier]);

  // Best Line Finder: fetch comparison data when picks change
  useEffect(() => {
    if (oddsLocked || !recs.length) return;
    const gameIds = [
      ...new Set(
        recs.flatMap((r) => (r.legs || []).map((l) => l.gameId).filter(Boolean) as string[]),
      ),
    ];
    if (gameIds.length > 0) fetchComparison(gameIds, selectedSportsbook);
  }, [recs, selectedSportsbook, fetchComparison, oddsLocked]);

  // — Generation logic (delegates to generatePicksFn) —

  const currentMode = analysisConfig.outputOptions?.analysisMode ?? 'standard';
  const modeQuota = quotaUsage ? getQuotaForMode(quotaUsage, currentMode) : null;

  const generatePicks = async (forceRefresh = false): Promise<void> =>
    generatePicksFn({
      forceRefresh,
      selectedGames,
      selectedMarkets,
      bannedPlayers,
      analysisConfig,
      selectedLeague,
      user,
      selectedProvider,
      isGenerating,
      modeQuota,
      startGeneration,
      finishGeneration,
      saveToHistory,
      setActivePicks,
      setPendingPicks,
      hasActivePicks,
      getAutoApplyPicks,
      decrementQuotaLocally,
      authFetch,
      startStream: _startStream,
      setError,
      setApiHealth,
      startHealthPolling,
      setLastFreshFetch,
      setIsConfigCollapsed,
      showCoin,
      closeConfigPanel,
      setShowUpgradeModal,
      scrollToParlays: () =>
        parlaysRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    });

  const refreshScoresOnly = async (): Promise<void> =>
    refreshScoresOnlyFn({ recs, activePicks, authFetch, setActivePicks });

  const toggleGame = (gameId: string): void => {
    const toggledGame = games.find((g) => g.id === gameId);
    if (!toggledGame) return;
    setSelectedGames((prev) => {
      if (prev.includes(gameId)) return prev.filter((id) => id !== gameId);
      return [...prev, gameId];
    });
  };

  const toggleMarket = (marketKey: string): void => {
    setSelectedMarkets((prev) =>
      prev.includes(marketKey) ? prev.filter((k) => k !== marketKey) : [...prev, marketKey],
    );
  };

  // — Parlay tracking (via useParlayTracking hook) —

  const {
    trackingKeys,
    trackingError,
    isParlayTracked,
    trackParlay,
    handleTrackHealthyLegs,
    handleAddToBuilder,
    setTrackingError,
  } = useParlayTracking({
    trackedParlays,
    trackedParlayStats,
    trackParlayProp,
    recs,
    inlineScores,
    selectedGames,
    generationId,
    selectedSportsbook,
    setError,
    setShowUpgradeModal,
  });

  // — Computed values —

  const leagueGames = useMemo(
    () => filterGamesByLeague(games, selectedLeague),
    [games, selectedLeague],
  );

  const { todayGameCount, tomorrowGameCount } = useMemo(() => {
    const now = new Date();
    const todayStr = now.toDateString();
    const tmrw = new Date(now);
    tmrw.setDate(tmrw.getDate() + 1);
    const tomorrowStr = tmrw.toDateString();
    let today = 0;
    let tomorrow = 0;
    for (const g of leagueGames) {
      const d = new Date(g.commence_time).toDateString();
      if (d === todayStr) today++;
      else if (d === tomorrowStr) tomorrow++;
    }
    return { todayGameCount: today, tomorrowGameCount: tomorrow };
  }, [leagueGames]);

  const filteredRecs = filterParlays(recs, filters);
  const groupedRecs: RiskTierGroup[] = groupByRiskTier(filteredRecs, RISK_TIERS, filters.sortBy);

  // — Render —

  return (
    <div className="flex flex-col @5xl:flex-row gap-4 relative">
      {/* Main Content Area */}
      <div className="flex-1 min-w-0 flex flex-col gap-4">
        {/* Dashboard hero when no results yet */}
        {recs.length === 0 && !isGenerating && (
          <PicksDashboardHero
            user={user}
            selectedLeague={selectedLeague}
            leagueGames={leagueGames}
            loadingGames={loadingGames}
            todayGameCount={todayGameCount}
            tomorrowGameCount={tomorrowGameCount}
            trackedParlays={trackedParlays}
            trackedParlayStats={trackedParlayStats}
          />
        )}

        {/* Betting tip: new user hero */}
        {recs.length === 0 && !isGenerating && <BettingTipBanner tip={BETTING_TIPS.newUserHero} />}

        {/* Market Intelligence — always visible, independent of picks */}
        <MarketIntelligence />

        {/* ML Analytics — CLV trends + Bankroll simulation */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <ClvTrendsPanel />
          </div>
          <div className="flex-1">
            <BankrollSimPanel />
          </div>
        </div>

        {/* Config section (collapsible) */}
        <PicksConfigSection
          recs={recs}
          isConfigCollapsed={isConfigCollapsed}
          onToggleCollapsed={() => setIsConfigCollapsed((prev) => !prev)}
          onCollapseConfig={() => setIsConfigCollapsed(true)}
          selectedLeague={selectedLeague}
          onLeagueChange={setSelectedLeague}
          leagueGames={leagueGames}
          loadingGames={loadingGames}
          refreshingGames={refreshingGames}
          onRefreshGames={handleRefreshGames}
          selectedGames={selectedGames}
          onToggleGame={toggleGame}
          onSelectAllGames={() =>
            setSelectedGames(
              groupGamesByDay(leagueGames).flatMap((day) => day.games.map((g) => g.id || '')),
            )
          }
          onDeselectAllGames={() => setSelectedGames([])}
          selectedMarkets={selectedMarkets}
          onToggleMarket={toggleMarket}
          bannedPlayers={bannedPlayers}
          availablePlayers={availablePlayers}
          loadingPlayers={loadingPlayers}
          onAddPlayer={addToBanList}
          onAddPlayers={addPlayersToBanList}
          onRemovePlayer={removeFromBanList}
          onRemovePlayers={removePlayersFromBanList}
          showDetailedAnalysis={showDetailedAnalysis}
          onShowDetailedAnalysisChange={setShowDetailedAnalysis}
          isAdmin={user?.role === 'admin'}
          selectedProvider={selectedProvider}
          onProviderChange={setSelectedProvider}
          apiHealth={apiHealth}
          onRetryHealth={checkApiHealth}
          isGenerating={isGenerating}
          wasCached={wasCached}
          onGenerate={generatePicks}
          modeQuota={modeQuota}
          analysisMode={currentMode as AnalysisMode}
          onShowUpgrade={() => setShowUpgradeModal(true)}
          sportsbookName={sportsbookName}
          error={error}
          onDismissError={() => setError(null)}
          showUpgradeModal={showUpgradeModal}
          onDismissUpgrade={() => setShowUpgradeModal(false)}
          quotaUsage={quotaUsage}
          updatePreferences={updatePreferences}
          setSelectedGames={setSelectedGames}
        />

        {/* View toggle tabs — shown when generating with existing picks, OR when pending picks are queued */}
        {((isGenerating && recs.length > 0) || (pendingPicks && !isGenerating)) && (
          <PicksViewToggle
            viewMode={viewMode}
            onSelectCurrent={() => setViewMode('current')}
            onSelectStreaming={() => setViewMode('streaming')}
            onApplyPending={handleApplyPending}
            currentPicksCount={recs.length}
            streamingParlaysCount={streamingParlays.length}
            pendingPicks={pendingPicks}
            isGenerating={isGenerating}
          />
        )}

        {/* Streaming preview — shows when actively streaming */}
        {isGenerating && (viewMode === 'streaming' || recs.length === 0) && (
          <div ref={parlaysRef}>
            <StreamingPicksPreview
              games={games}
              onTrack={trackParlay}
              isParlayTracked={isParlayTracked}
              trackingKeys={trackingKeys}
              onChartClick={setChartModal}
              showDetailedAnalysis={showDetailedAnalysis}
              oddsComparisons={oddsComparisons}
              onScoreRefresh={refreshScoresOnly}
            />
          </div>
        )}

        {/* Betting tip: first generation */}
        {recs.length > 0 && (!isGenerating || viewMode === 'current') && (
          <BettingTipBanner tip={BETTING_TIPS.firstGeneration} />
        )}

        {/* Results Section */}
        {recs.length > 0 && (!isGenerating || viewMode === 'current') && (
          <div ref={parlaysRef}>
            <PicksResultsSection
              recs={recs}
              inlineScores={inlineScores}
              generationId={generationId}
              games={games}
              statsWarning={statsWarning}
              wasCached={wasCached}
              lastFreshFetch={lastFreshFetch}
              sportsbookName={sportsbookName}
              strategyStats={strategyStats}
              predictionStats={predictionStats}
              calibration={calibration}
              filters={filters}
              onFiltersChange={setFilters}
              filteredRecs={filteredRecs}
              groupedRecs={groupedRecs}
              collapseState={collapseState}
              onToggleTierCollapse={toggleTierCollapse}
              onToggleParlayCollapse={toggleParlayCollapse}
              showDetailedAnalysis={showDetailedAnalysis}
              oddsComparisons={oddsComparisons}
              onTrack={trackParlay}
              isParlayTracked={isParlayTracked}
              trackingKeys={trackingKeys}
              onChartClick={setChartModal}
              onScoreRefresh={refreshScoresOnly}
            />
          </div>
        )}
      </div>

      {/* Tracked Parlays Summary — links to full My Bets view */}
      {onNavigateToParlays && (
        <div className="hidden @5xl:block w-64 shrink-0">
          <div className="sticky top-20">
            <TrackedParlaysSummaryWidget
              stats={trackedParlayStats}
              onNavigateToParlays={onNavigateToParlays}
            />
          </div>
        </div>
      )}

      {/* Track error modal — outside config panel so it's visible when collapsed */}
      {error && (error.type === 'track_error' || error.type === 'track_quota_exceeded') && (
        <ErrorModal
          error={error}
          onDismiss={() => setError(null)}
          onUpgrade={() => setShowUpgradeModal(true)}
        />
      )}

      {/* All Modals */}
      <PicksModals
        chartModal={chartModal}
        onCloseChart={() => setChartModal(null)}
        isGenerating={isGenerating}
        analysisMode={(analysisConfig.outputOptions?.analysisMode ?? 'standard') as AnalysisMode}
        onDismissGeneration={dismissGenerationModal}
        onCancelGeneration={() => {
          authAwareFetch('/api/picks/cancel', { method: 'POST' }).catch(() => {});
          cancelGeneration();
        }}
        isGenerationDismissed={isGenerationDismissed}
        trackingError={trackingError}
        onCloseTrackingError={() => setTrackingError(null)}
        onTrackHealthyLegs={handleTrackHealthyLegs}
        onAddToBuilder={handleAddToBuilder}
      />
    </div>
  );
}
